import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaToggleOn, FaToggleOff, FaSms } from "react-icons/fa";
import toast from "react-hot-toast";

import {
  useGetTenantsQuery,
  useToggleTenantMutation,
  useUpdateTenantPlanMutation,
  useUpdateTenantSmsMutation 
} from "../toolkitServices/adminTenantApi";

const Badge = ({ children, color }) => (
  <span
    className={`px-2 py-0.5 text-[10px] rounded-full font-semibold ${color}`}
  >
    {children}
  </span>
);

const TenantList = () => {
  const { data, isLoading, error } = useGetTenantsQuery();
  const [toggleTenant] = useToggleTenantMutation();
  const [updateTenantPlan] = useUpdateTenantPlanMutation();
const [updateTenantSms] = useUpdateTenantSmsMutation();
  const tenants = data?.data || [];
const [isSmsModalOpen, setIsSmsModalOpen] = React.useState(false);
const [selectedTenant, setSelectedTenant] = React.useState(null);

const [smsForm, setSmsForm] = React.useState({
  balance: "",
  totalUsed: "",
});
  /* -------------------- toggle active/inactive -------------------- */
  const handleToggle = async (tenant) => {
    try {
      await toggleTenant({
        tenantId: tenant.tenantId,
        isActive: !tenant.isActive,
      }).unwrap();

      toast.success(
        tenant.isActive
          ? "Tenant deactivated successfully"
          : "Tenant activated successfully",
      );
    } catch (err) {
      toast.error(err?.data?.msg || "Action failed");
    }
  };

  /* ---------------------- change subscription ---------------------- */
  const handlePlanChange = async (tenantId, plan) => {
    try {
      let price = 0;

      if (plan === "monthly") price = 999;
      if (plan === "yearly") price = 9999;
      if (plan === "free") price = 0;

      await updateTenantPlan({
        tenantId,
        plan,
        price,
      }).unwrap();

      toast.success(`Plan changed to ${plan}`);
    } catch (err) {
      toast.error(err?.data?.msg || "Plan update failed");
    }
  };

  /* ---------------------- days left helper ---------------------- */
  const getDaysLeft = (expiryDate, plan) => {
    if (plan === "free") return "Unlimited";
    if (!expiryDate) return "-";

    const diff = new Date(expiryDate).getTime() - new Date().getTime();

    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days > 0 ? `${days} days` : "Expired";
  };


  const handleUpdateSms = async () => {
  try {
    console.log(selectedTenant)
    console.log(smsForm)
    await updateTenantSms({
      tenantId: selectedTenant.tenantId,
      balance: Number(smsForm.balance),
      totalUsed: Number(smsForm.totalUsed),
    }).unwrap();
    
    toast.success("SMS updated");
    setIsSmsModalOpen(false);
  } catch (err) {
    toast.error(err?.data?.msg || "Update failed");
  }
};
  return (
    <div className="bg-white shadow-lg rounded-2xl flex flex-col h-[calc(100vh-140px)] md:h-[calc(100vh-70px)]">
      {/* Header */}
      <div className="px-5 py-3 rounded-t-2xl bg-[#9481ce]  text-white">
        <h2 className="text-lg font-semibold">Tenant Management</h2>
        <p className="text-xs text-violet-100">
          Manage subscription plans and activation status
        </p>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="min-w-full text-sm text-left">
          <thead className="bg-[#e6e1f5] text-[#4b3d91] uppercase text-xs sticky top-0">
            <tr>
              <th className="px-4 py-2">Tenant ID</th>
              <th className="px-4 py-2">Owner</th>
              <th className="px-4 py-2">Plan</th>
              <th className="px-4 py-2">Expiry</th>
              <th className="px-4 py-2">Days Left</th>
              <th className="px-4 py-2 text-center">Users</th>
              <th className="px-4 py-2 text-center">Status</th>
              <th className="px-4 py-2 text-center">Change Plan</th>
              <th className="px-4 py-2 text-center">SMS Balance</th>
              <th className="px-4 py-2 text-center">SMS Status</th>
              <th className="px-4 py-2 text-center">Action</th>
            </tr>
          </thead>

          <tbody className="bg-white text-gray-700">
            <AnimatePresence>
              {tenants.length ? (
                tenants.map((t, i) => (
                  <motion.tr
                    key={t.tenantId}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2, delay: i * 0.02 }}
                    className="border-t hover:bg-[#f4f0ff] transition"
                  >
                    <td className="px-4 py-2 font-medium">{t.tenantId}</td>

                    <td className="px-4 py-2">
                      <div className="flex flex-col">
                        <span>{t?.ownerName}</span>
                        <span className="text-xs text-gray-400">
                          {t?.ownerEmail}
                        </span>
                        <span className="text-xs text-gray-400">
                          {t?.ownerPhone}
                        </span>
                      </div>
                    </td>

                    {/* Plan Badge */}
                    <td className="px-4 py-2">
                      {t.plan === "free" && (
                        <Badge color="bg-gray-200 text-gray-700">Free</Badge>
                      )}
                      {t.plan === "trial" && (
                        <Badge color="bg-yellow-100 text-yellow-700">
                          Trial
                        </Badge>
                      )}
                      {t.plan === "monthly" && (
                        <Badge color="bg-blue-100 text-blue-700">Monthly</Badge>
                      )}
                      {t.plan === "yearly" && (
                        <Badge color="bg-green-100 text-green-700">
                          Yearly
                        </Badge>
                      )}
                    </td>

                    {/* Expiry */}
                    <td className="px-4 py-2 text-xs">
                      {t.plan === "free"
                        ? "No Expiry"
                        : t.expiryDate
                          ? new Date(t.expiryDate).toLocaleDateString()
                          : "-"}
                    </td>

                    {/* Days Left */}
                    <td className="px-4 py-2 text-xs font-medium">
                      {getDaysLeft(t.expiryDate, t.plan)}
                    </td>

                    <td className="px-4 py-2 text-center font-semibold">
                      {t.userCount}
                    </td>

                    {/* Status */}
                    <td className="px-4 py-2 text-center">
                      {t.isActive ? (
                        <Badge color="bg-green-100 text-green-700">
                          Active
                        </Badge>
                      ) : (
                        <Badge color="bg-red-100 text-red-700">Inactive</Badge>
                      )}
                    </td>

                    {/* Change Plan */}
                    <td className="px-4 py-2 text-center">
                      <select
                        onChange={(e) =>
                          handlePlanChange(t.tenantId, e.target.value)
                        }
                        defaultValue=""
                        className="border text-xs rounded px-2 py-1"
                      >
                        <option value="" disabled>
                          Select
                        </option>
                        <option value="free">Free</option>
                        <option value="trial">Trial</option>
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </td>

                    {/* SMS Balance */}
<td className="px-4 py-2 text-center font-semibold">
  {t?.smsBalance < 500 ? (
    <span className="text-red-500">{t?.smsBalance || 0}</span>
  ) : (
    <span className="text-green-600">{t?.smsBalance || 0}</span>
  )}
</td>

{/* SMS Enabled / Disabled */}
<td className="px-4 py-2 text-center">
  {t.smsEnabled ? (
    <Badge color="bg-green-100 text-green-700">Enabled</Badge>
  ) : (
    <Badge color="bg-gray-200 text-gray-600">Disabled</Badge>
  )}
</td>

                    {/* Toggle */}
                    <td className="px-4 py-2 text-center">
  <div className="flex flex-row items-center gap-2">
    
    {/* Toggle Button */}
    <button
      onClick={() => handleToggle(t)}
      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold transition ${
        t.isActive
          ? "bg-rose-100 text-rose-600 hover:bg-rose-200"
          : "bg-green-100 text-green-600 hover:bg-green-200"
      }`}
    >
      {t.isActive ? (
        <>
          <FaToggleOff  title="Deactivate"/>
        </>
      ) : (
        <>
          <FaToggleOn  title="Activate"/> 
        </>
      )}
    </button>

    {/* 🔥 Recharge Button */}
    <button
     onClick={() => {
    setSelectedTenant(t);
    setSmsForm({
      balance: t.smsBalance || 0,
      totalUsed: t.smsUsed || 0,
    });
    setIsSmsModalOpen(true);
  }}
      className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-violet-100 text-violet-700 hover:bg-violet-200 transition"
    >
      <FaSms title="Recharge"/>
    </button>

  </div>
</td>
                  </motion.tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="text-center py-6 text-gray-500">
                    {isLoading ? "Loading tenants..." : "No tenants found"}
                  </td>
                </tr>
              )}
            </AnimatePresence>
          </tbody>
        </table>
      </div>

      {error && (
        <div className="text-red-500 text-xs px-4 py-2 border-t">
          Failed to load tenants
        </div>
      )}



{isSmsModalOpen && (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
    <div className="bg-white rounded-xl p-6 w-[320px] shadow-lg">
      
      <h3 className="text-sm font-semibold mb-4">
        SMS Recharge - {selectedTenant?.tenantId}
      </h3>

      {/* Balance */}
      <div className="mb-3">
        <label className="text-xs text-gray-500">Balance</label>
        <input
          type="number"
          value={smsForm.balance}
          onChange={(e) =>
            setSmsForm({ ...smsForm, balance: e.target.value })
          }
          className="w-full border rounded px-2 py-1 text-sm"
        />
      </div>

      {/* Total Used */}
      <div className="mb-4">
        <label className="text-xs text-gray-500">Total Used</label>
        <input
          type="number"
          value={smsForm.totalUsed}
          onChange={(e) =>
            setSmsForm({ ...smsForm, totalUsed: e.target.value })
          }
          className="w-full border rounded px-2 py-1 text-sm"
        />
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-2">
        <button
          onClick={() => setIsSmsModalOpen(false)}
          className="px-3 py-1 text-xs bg-gray-200 rounded"
        >
          Cancel
        </button>

        <button
          onClick={handleUpdateSms}
          className="px-3 py-1 text-xs bg-violet-600 text-white rounded"
        >
          Update
        </button>
      </div>

    </div>
  </div>
)}

    </div>
  );
};

export default TenantList;
